<template>
  <div
    class="shop-item"
    :class="{ 'shop-item--placeholder': placeholder, 'shop-item--readonly': readonly }"
    v-on="$listeners"
  >
    <ProductCard v-if="!placeholder" :value="valueComp" :selected="value.selected" theme="shop">
      <template #subtitle>
        <div class="shop-item__specification">
          <div>{{ $t('price') }}</div>
          <strong>
            <Helper
              v-if="!value.isDefault && pricing.type !== 'request' && !areaMissing"
              :title="`${$t('priceCalculation')} (${$t(`helpers:shopPricingMethodTitle:${pricingMethod}`)})`"
              :body="helperBody"
            />

            <template v-if="value.isDefault">{{ $t('standard') }}</template>
            <template v-else-if="pricing.type === 'request'">{{ $t('priceOnRequest') }}</template>
            <span v-else-if="areaMissing" class="text--error text--small">{{ $t('priceMissingArea') }}</span>
            <template v-else>
              <span v-if="pricingMethod === 'add' && hasPricingOverrideDiff" class="text--linethrough fade">
                <template v-if="value.product.pricing.type === 'request'">{{ $t('priceOnRequest') }}</template>
                <template v-else>{{ getGross({ pricingMethod, ignoreOverride: true }) | Currency }}</template>
              </span>
              <span>{{ getGross({ pricingMethod }) | Currency }}</span>
            </template>
          </strong>
        </div>

        <div v-if="pricingMethod === 'diff' && pricing.type !== 'request'" class="shop-item__specification">
          <div>{{ $t('ordinaryProductPrice') }}</div>
          <div>
            <span :class="{ 'text--linethrough': hasPricingOverrideDiff, fade: hasPricingOverrideDiff }">
              <template v-if="value.product.pricing.type === 'request'">{{ $t('priceOnRequest') }}</template>
              <template v-else>{{ getGross({ ignoreOverride: true, area: 1, round: false }) | Currency }}</template>
            </span>
            <span v-if="hasPricingOverrideDiff">{{ getGross({ area: 1, round: false }) | Currency }}</span>
            <template v-if="pricing.type === 'area'">/ m²</template>
          </div>
        </div>

        <div v-if="value.quantity.enabled" class="shop-item__specification">
          <div>{{ $t('quantity') }}</div>
          <div>{{ [value.quantity.min, value.quantity.max].filter(Boolean).join('-') }} {{ $t('piecesShort') }}</div>
        </div>
        <!-- <div v-if="hasVariants" class="shop-item__specification">
          <div>{{ $tc('variants', 0) }}</div>
          <div>{{ value.product.variants.items.length }}</div>
        </div> -->

        <div class="shop-item__vat">{{ $t('pricesIncVat') }}</div>

        <DropdownComponent v-if="!readonly" class="shop-item__actions">
          <ButtonComponent :icon="['far', 'ellipsis-v']" />
          <template #dropdown>
            <ButtonComponent
              v-if="!(value.isDefault && value.selected)"
              :label="$t(value.selected ? 'resetSelection' : 'select')"
              :icon="['fal', value.selected ? 'square' : 'check-square']"
              @click="$emit(value.selected ? 'unselect' : 'select')"
            />
            <hr />
            <ButtonComponent
              :label="$t('settings')"
              :icon="['fal', 'cog']"
              @click="
                () => {
                  showSettings = true
                  shopTracker('ProductSettings')
                }
              "
            />
            <ButtonComponent
              :label="$t('move')"
              :icon="['fal', 'arrows']"
              @click="
                () => {
                  showMove = true
                  shopTracker('ProductMove')
                }
              "
            />
            <ButtonComponent
              :label="$t('edit', [$tc('product', 1).toLowerCase()])"
              :icon="['fal', 'pen']"
              @click="
                () => {
                  $emit('edit', value.product)
                  shopTracker('ProductEdit')
                }
              "
            />
            <ButtonComponent
              :label="$t('copy', [$tc('product', 1).toLowerCase()])"
              :icon="['fal', 'copy']"
              @click="
                () => {
                  $emit('copy', value.product)
                  shopTracker('ProductCopy')
                }
              "
            />
            <ButtonComponent
              tag="router-link"
              :to="{ name: 'product-edit', params: { id: value.product.id } }"
              :label="$t('gotoItem', [$tc('product', 1).toLowerCase()])"
              :icon="['fal', 'external-link']"
            />
            <hr />
            <ButtonComponent :label="$t('remove')" :icon="['fal', 'times']" @click="$emit('remove')" />
          </template>
        </DropdownComponent>
      </template>
      <template #badges>
        <Badge v-if="hasPricingOverrideDiff">{{ $t('priceAdjusted') }}</Badge>
      </template>
    </ProductCard>
    <div
      v-if="placeholder || $slots.placeholder"
      class="shop-item__placeholder"
      :class="{ 'shop-item__placeholder--selected': $path('selected', value || {}) }"
    >
      <div v-if="$path('selected', value || {})" class="product-card__selection">
        {{ $t('selected') }}
        <FontAwesomeIcon :icon="['fas', 'check-circle']" />
      </div>
      <slot name="placeholder">
        <span>{{ placeholder }}</span>
        <FontAwesomeIcon :icon="['fal', 'plus']" class="ml-1" />
      </slot>
    </div>
    <Settings
      v-if="showSettings"
      :value="value"
      @input="updateSettings"
      @close="showSettings = false"
      :disabled-settings="disabledSettings"
    />
    <Move v-if="showMove" :value="value" :categories="categories" @input="moveProduct" @close="showMove = false" />
  </div>
</template>

<script>
import { Currency } from '@/filters'
import { getGross } from '@/components/Product/utils'

import Badge from '@/components/Badge'
import ProductCard from '@/components/Product/Card'
import { DropdownComponent } from 'vue-elder-dropdown'
import Settings from '@/components/Shop/Items/Settings'
import Move from '@/components/Shop/Items/Move'
import Helper from '@/components/HelperPopover'

export default {
  inject: ['shopTracker'],
  props: {
    value: Object,
    area: Number,
    categories: Array,
    defaultProduct: Object,
    placeholder: [String, Boolean],
    readonly: Boolean,
    pricingMethod: String,
  },
  data() {
    return {
      showSettings: false,
      showMove: false,
      rules: {
        'pricing.value': 'required_unless:pricing.type,request|min:0',
        'pricing.margin.value': 'required_unless:pricing.type,request|min:0',
      },
    }
  },
  computed: {
    helperBody() {
      let content = [this.$t(`helpers:shopPricingMethodDescription:${this.pricingMethod}`)]
      if (this.pricing.rounding !== 'none')
        content.push(this.$t('priceIsRoundedToNearestX', { price: Currency(parseInt(this.pricing.rounding)) }))
      return content.join('<br><br>')
    },
    isArea() {
      return this.pricing.type === 'area'
    },
    areaMissing() {
      return this.isArea && !this.area
    },
    valueComp() {
      const conditions = Object.values(this.value.conditions).reduce((agg, curr) => {
        return agg + curr.length
      }, 0)
      return { conditions, ...this.value.product }
    },
    disabledSettings() {
      let values = new Set()

      if (this.value.isDefault) ['conditions', 'quantity', 'referencePropertyChanges'].forEach(v => values.add(v))
      if (this.isArea) values.add('quantity')
      if (this.pricingMethod === 'diff' && this.getGross() !== this.getGross({ pricingMethod: this.pricingMethod }))
        values.add('quantity')

      return Array.from(values)
    },
    isScoped() {
      return this.value.product.isScoped || Boolean(this.$path('product.reference.ref', this.value))
    },
    pricing() {
      return this.value.overrides.pricing || this.value.product.pricing || {}
    },
    hasPricingOverrideDiff() {
      return this.hasPricingOverride && this.getGross() !== this.getGross({ ignoreOverride: true })
    },
    hasPricingOverride() {
      return Boolean(this.value.overrides.pricing)
    },
    hasVariants() {
      return Boolean(this.value.product.variants && this.value.product.variants.items.length)
    },
  },
  methods: {
    updateSettings(data) {
      Object.assign(this.value, data)

      if (this.$path('overrides.pricing.type', data) === 'area') this.value.quantity.enabled = false

      this.showSettings = false
    },
    moveProduct(data) {
      this.showMove = false
      this.$emit('move', data)
    },
    getGross(options = {}) {
      const { pricingMethod = 'add', ignoreOverride = false, area = this.area, round = true } = options

      if (!this.value) return

      let pricing = ignoreOverride ? this.value.product.pricing : this.pricing

      return getGross(pricing, {
        round,
        area,
        pricingMethod,
        defaultProduct: this.defaultProduct,
        product: this.value.product,
      })
    },
  },
  filters: {
    Currency,
  },
  components: {
    ProductCard,
    DropdownComponent,
    Badge,
    Helper,
    Settings,
    Move,
  },
}
</script>

<style lang="scss">
.shop-item {
  &__placeholder {
    color: css-alpha('dark', 0.5);
    border: 1px dashed var(--border-color);
    border-radius: var(--border-radius-md);
    background-color: css-alpha('border-color', 0.2);
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 250px;
    height: 100%;
    position: relative;
  }

  &__specification {
    display: flex;
    justify-content: space-between;

    gap: 0.5rem;
    font-size: var(--font-size-small);

    .text--linethrough {
      font-weight: normal;
    }

    strong {
      font-size: 1rem;
    }

    &:first-child {
      margin-bottom: 1rem;
      align-items: flex-end;
    }

    & > *:last-child {
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-end;
      gap: 0.25rem;

      line-height: 1.2;
    }

    & > *:first-child {
      white-space: nowrap;
      opacity: var(--opacity-muted);
      line-height: 1;
    }

    & + & {
      margin-top: 0.3rem;
    }
  }

  &__vat {
    opacity: 0.5;
    font-size: 0.7em;
    margin-top: 0.25rem;
    text-align: right;
  }

  &__actions {
    position: absolute;
    top: 1rem;
    right: 1rem;
  }

  &__price {
    margin-top: 0.25rem;
  }

  &--readonly {
    cursor: default;
  }

  &--readonly &__placeholder {
    border-style: solid;
    cursor: default;
  }

  .product-card__demo {
    display: none;
  }

  .elder-dropdown {
    &__trigger {
      background-color: white;
      border-radius: var(--border-radius);
      box-shadow: var(--shadow-lg);

      .elder-button__icon {
        padding: 0.5rem 1rem !important;
      }
    }
  }

  .product-card {
    height: 100%;

    .popover-component {
      margin-right: 0.25rem;
      translate: 0 -1px;
    }

    .card__content {
      display: flex;
      flex-direction: column;
      padding-top: 1rem;
    }

    &__subtitle {
      font-size: 1em;
      opacity: 1;
    }
  }
}
</style>
